/**
 * @fileoverview AR-670 — Google Classroom integration domain types.
 *
 * RECONCILED (2026-08-27) with the READ-ONLY backend contract of the branch
 * feat-classroom-link-only-courses, documented in
 * mosaic-backend/docs/frontend-integration/:
 * - google-workspace-connection.md (courses = local synced catalog)
 * - google-classroom-course-link.md (link existing courses only; POST /link
 *   runs the roster matching synchronously; 1 course = 1 project)
 * - google-classroom-student-mapping.md (matching read-only by email; the
 *   backend never invites/enrolls anyone — students[].status is always
 *   "enrolled" and the summary reports matched/unmatched counts)
 * Grade push (phase 4) has NO backend — its types remain from stage 1.
 *
 * Backend responses arrive camelCased via the humps interceptor (helpers/api).
 */

/** Connection status. Backend omits `status` when there is no connection. */
export type SchoolConnectionStatus = 'disconnected' | 'connected' | 'needs_reconnection'

export interface SchoolConnection {
  connected: boolean
  status: SchoolConnectionStatus
  googleEmail?: string
  workspaceDomain?: string | null
  connectedAt?: string
}

/** Course lifecycle in Google Classroom (closed enum of the Classroom API). */
export type ClassroomCourseState = 'ACTIVE' | 'ARCHIVED' | 'PROVISIONED' | 'DECLINED' | 'SUSPENDED'

/** Course option from GET /google_workspace_connection/courses (catalog). */
export interface ClassroomCourseOption {
  id: string
  name: string
  section: string | null
  courseState?: ClassroomCourseState
  alternateLink?: string
  /** Project already linked to this course (1 course = 1 project), or null. */
  linkedProject?: { id: string; name: string } | null
}

/** Full payload of the local synced catalog. */
export interface ClassroomCourseCatalog {
  courses: ClassroomCourseOption[]
  syncedAt: string | null
  connectionStatus: SchoolConnectionStatus
}

/**
 * The picker only offers courses a link can succeed on: ACTIVE ones (Google
 * rejects writes on archived/suspended) that are not already linked to
 * another project — linking those would just hit the new 422 `course_id`.
 */
export function isLinkableCourse(course: ClassroomCourseOption): boolean {
  return (!course.courseState || course.courseState === 'ACTIVE') && !course.linkedProject
}

/* ------------------------------------------------------------------ */
/* Project ↔ course link (phase 2)                                     */
/* ------------------------------------------------------------------ */

/** Matching is read-only: a mapped student is always simply "enrolled". */
export type RosterStudentStatus = 'enrolled'

/** One MATCHED project student in the persisted roster (GET of the card). */
export interface RosterStudent {
  studentId: string
  name: string
  email: string | null
  status: RosterStudentStatus
  /** Kept by the backend for shape stability; always null now. */
  failureReason: string | null
}

export type RosterUnmatchedReason = 'no_email' | 'not_found_in_course'

/** One project student the matching could NOT map. `reason` is present in the
 *  link/sync_roster summary and ABSENT in the GET of the card. */
export interface RosterUnmatchedStudent {
  studentId: string
  name: string
  email: string | null
  reason?: RosterUnmatchedReason
}

/** Summary returned by POST /link and POST sync_roster (drives the toaster). */
export interface RosterSummary {
  mappedStudentsCount: number
  matchedCount: number
  unmatchedCount: number
  unmatched: RosterUnmatchedStudent[]
}

/**
 * Composite state of the project's Classroom card. `connectionStatus` is NOT
 * part of the card endpoint — the adapter composes it with the school
 * connection so the component keeps a single source.
 */
export interface ProjectLinkState {
  connectionStatus: SchoolConnectionStatus
  linked: boolean
  syncEnabled: boolean
  /** Google Classroom course id (course.google_course_id). */
  courseId: string | null
  courseName: string | null
  /** course.alternate_link */
  courseLink: string | null
  /** 'created' only exists on legacy links — courses are never created now. */
  origin?: 'created' | 'linked' | null
  mappedStudentCount: number
  lastRosterSyncAt?: string | null
  /** Persisted per-student mapping — matched students only (GET only). */
  rosterStudents?: RosterStudent[]
  /** Project students the matching could not map (GET only, no reason). */
  unmatchedStudents?: RosterUnmatchedStudent[]
}

/**
 * Result of link/sync_roster. POST /link runs the matching synchronously;
 * `roster` is null ONLY when that matching run failed — the link itself is
 * kept and `rosterError` explains why.
 */
export interface ProjectLinkResult {
  linked: boolean
  syncEnabled: boolean
  courseId: string | null
  courseName: string | null
  courseLink: string | null
  roster: RosterSummary | null
  rosterError: string | null
}

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

/**
 * Normalized error codes derived from the backend's 422 payloads
 * ({ "connection": [...] } / { "course": [...] }) plus phase 3/4 codes.
 */
export type ClassroomErrorCode =
  /** 401 — the profile cannot manage this school's integration (hide the UI). */
  | 'unauthorized'
  | 'not_connected'
  | 'needs_reconnection'
  | 'already_linked'
  /** 422 course_id — the COURSE is taken by another project (1:1 rule). */
  | 'course_already_linked'
  | 'not_linked'
  /** 422 connection — the tutor who linked disconnected; unlink + link again. */
  | 'connection_gone'
  | 'course_not_found'
  | 'sync_disabled'
  | 'not_course_teacher'
  | 'classroom_api_error'
  // phase 4 (grade push) — no backend yet, kept for the mocked section:
  | 'student_not_linked'
  | 'no_submission'
  | 'not_assignment_owner'

/** Typed error thrown by adapters so the UI maps `code` to i18n copy. */
export class ClassroomPortError extends Error {
  code: ClassroomErrorCode

  constructor(code: ClassroomErrorCode, message?: string) {
    super(message || code)
    this.name = 'ClassroomPortError'
    this.code = code
  }
}

export function isClassroomPortError(error: unknown): error is ClassroomPortError {
  return error instanceof ClassroomPortError
}

/* ------------------------------------------------------------------ */
/* Grade push (phase 4 — NO backend yet; mocked section only)          */
/* ------------------------------------------------------------------ */

export interface ClassroomAssignment {
  activityId: string
  activityTitle: string
}

/** studentId -> activityId -> 1–5 grade already pushed to Classroom. */
export type ClassroomGradesByStudent = Record<string, Record<string, number>>

export interface GradePushResult {
  gcGrade: number | null
  pushed: boolean
  draft: boolean
}

